import React from 'react';
import styled, { css, keyframes } from 'styled-components';

// Shared building blocks. Colors, radii and type all come from the tokens in index.css.

// Feedback lives on the press: scale down instantly on pointer-down, ease back on release.
export const pressable = css`
  -webkit-tap-highlight-color: transparent;
  transition:
    transform 180ms var(--ease-out),
    background-color 160ms ease,
    border-color 160ms ease,
    color 160ms ease,
    box-shadow 160ms ease,
    opacity 160ms ease;

  &:active:not(:disabled) {
    transform: scale(0.97);
    transition-duration: 60ms;
  }

  @media (prefers-reduced-motion: reduce) {
    &:active:not(:disabled) {
      transform: none;
    }
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: calc(var(--page-max) + 2 * var(--gutter));
  margin: 0 auto;
  padding: 0 var(--gutter);
`;

export const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--separator);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: ${(p) => (p.$flush ? '0' : 'clamp(20px, 3vw, 32px)')};
  min-width: 0;
`;

// ------------------------------------------------------------------ type

export const Eyebrow = styled.p`
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 12px;
`;

export const DisplayTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 6.2vw, 4.75rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  font-weight: 700;
  color: var(--text);
  text-wrap: balance;
`;

export const PageTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2.125rem, 4.6vw, 3.25rem);
  line-height: 1.07;
  letter-spacing: -0.03em;
  font-weight: 700;
  color: var(--text);
  text-wrap: balance;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(1.625rem, 3.2vw, 2.25rem);
  line-height: 1.12;
  letter-spacing: -0.028em;
  font-weight: 700;
  color: var(--text);
  text-wrap: balance;
`;

export const CardTitle = styled.h3`
  font-size: 1.1875rem;
  line-height: 1.25;
  letter-spacing: -0.018em;
  font-weight: 600;
  color: var(--text);
`;

export const Lead = styled.p`
  font-size: clamp(1.0625rem, 1.7vw, 1.3125rem);
  line-height: 1.5;
  letter-spacing: -0.012em;
  color: var(--text-secondary);
  max-width: 40em;
  text-wrap: pretty;
`;

export const Muted = styled.p`
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-secondary);
`;

export const PageHeader = styled.header`
  padding: clamp(40px, 7vw, 88px) 0 clamp(28px, 4vw, 44px);

  ${Lead} {
    margin-top: 16px;
  }
`;

export const SectionHeader = styled.div`
  margin-bottom: clamp(24px, 3.5vw, 40px);

  ${Lead} {
    margin-top: 12px;
  }
`;

// ------------------------------------------------------------------ buttons

const buttonVariants = {
  primary: css`
    background: var(--accent-fill);
    color: var(--on-accent);

    &:hover:not(:disabled) {
      background: var(--accent-fill-hover);
    }
  `,
  secondary: css`
    background: var(--fill);
    color: var(--text);

    &:hover:not(:disabled) {
      background: var(--fill-strong);
    }
  `,
  outline: css`
    background: transparent;
    color: var(--text);
    border-color: var(--separator-strong);

    &:hover:not(:disabled) {
      background: var(--fill);
    }
  `,
};

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: ${(p) => (p.$size === 'sm' ? '36px' : '44px')};
  padding: 0 ${(p) => (p.$size === 'sm' ? '16px' : '22px')};
  border-radius: 980px;
  border: 1px solid transparent;
  font-size: ${(p) => (p.$size === 'sm' ? '0.875rem' : '0.9375rem')};
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  ${pressable}
  ${(p) => buttonVariants[p.$variant || 'primary']}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const TextLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--accent);
  font-size: 0.9375rem;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

// ------------------------------------------------------------------ segmented control

export const Segmented = styled.div`
  display: inline-flex;
  max-width: 100%;
  padding: 3px;
  gap: 2px;
  border-radius: 11px;
  background: var(--fill);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SegmentedItem = styled.button`
  flex: ${(p) => (p.$grow ? '1 1 0' : '0 0 auto')};
  appearance: none;
  border: 0;
  background: transparent;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  cursor: pointer;
  ${pressable}

  &:hover {
    color: var(--text);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--segment-active);
      color: var(--text);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
    `}
`;

// ------------------------------------------------------------------ inputs

export const inputStyles = css`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid var(--separator-strong);
  background: var(--surface);
  color: var(--text);
  font-size: 1rem;
  letter-spacing: -0.01em;
  transition: border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 4px var(--accent-ring);
  }
`;

export const Input = styled.input`
  ${inputStyles}
`;

const SearchWrap = styled.label`
  position: relative;
  display: block;

  svg {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-tertiary);
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  ${inputStyles}
  padding-left: 40px;
  background: var(--fill);
  border-color: transparent;

  &:focus {
    background: var(--surface);
  }

  &::-webkit-search-cancel-button {
    cursor: pointer;
  }
`;

export function SearchField(props) {
  return (
    <SearchWrap>
      <IconSearch size={17} />
      <SearchInput type="search" autoComplete="off" spellCheck={false} {...props} />
    </SearchWrap>
  );
}

export const FieldLabel = styled.span`
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 6px;
`;

// ------------------------------------------------------------------ feedback

const noticeTones = {
  info: css`
    background: var(--fill);
    --notice-icon: var(--text-secondary);
  `,
  success: css`
    background: var(--positive-tint);
    --notice-icon: var(--positive);
  `,
  error: css`
    background: var(--negative-tint);
    --notice-icon: var(--negative);
  `,
  warning: css`
    background: var(--warning-tint);
    --notice-icon: var(--warning);
  `,
};

const NoticeBox = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  line-height: 1.45;
  color: var(--text);
  ${(p) => noticeTones[p.$tone] || noticeTones.info}

  > svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--notice-icon);
  }

  strong {
    display: block;
    font-weight: 600;
    margin-bottom: 2px;
  }

  p {
    color: var(--text-secondary);
  }
`;

const noticeIcons = {
  info: IconInfo,
  success: IconCheckCircle,
  error: IconAlert,
  warning: IconAlert,
};

export function Notice({ tone = 'info', title, children, ...rest }) {
  const Glyph = noticeIcons[tone] || IconInfo;
  return (
    <NoticeBox $tone={tone} role={tone === 'error' ? 'alert' : 'status'} {...rest}>
      <Glyph size={18} />
      <div>
        {title && <strong>{title}</strong>}
        {children && (title ? <p>{children}</p> : children)}
      </div>
    </NoticeBox>
  );
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinnerRing = styled.span`
  display: inline-block;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  border: 2px solid var(--fill-strong);
  border-top-color: var(--text-secondary);
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

export function Spinner({ size = 18 }) {
  return <SpinnerRing $size={size} aria-hidden="true" />;
}

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: var(--text-secondary);
  font-size: 0.9375rem;
`;

export function Loading({ children }) {
  return (
    <LoadingRow role="status">
      <Spinner />
      <span>{children}</span>
    </LoadingRow>
  );
}

export const EmptyState = styled.div`
  text-align: center;
  padding: 44px 20px;
  color: var(--text-secondary);

  h3 {
    color: var(--text);
    font-size: 1.0625rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin-bottom: 6px;
  }

  p {
    font-size: 0.9375rem;
    max-width: 34em;
    margin: 0 auto;
  }
`;

// ------------------------------------------------------------------ data

export const rateTone = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 'neutral';
  return n > 0 ? 'positive' : 'negative';
};

export const Rate = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === 'positive' ? 'var(--positive)' : p.$tone === 'negative' ? 'var(--negative)' : 'var(--text)'};
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 980px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  background: ${(p) => (p.$accent ? 'var(--accent-tint)' : 'var(--fill)')};
  color: ${(p) => (p.$accent ? 'var(--accent)' : 'var(--text-secondary)')};
`;

export const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$active ? 'var(--positive)' : 'var(--text-tertiary)')};
`;

// ------------------------------------------------------------------ icons (stroke = currentColor)

function Svg({ size = 20, strokeWidth = 1.75, children, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconSearch(props) {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Svg>
  );
}

export function IconCheck(props) {
  return (
    <Svg {...props}>
      <path d="M5 12.5 10 17.5 19 7" />
    </Svg>
  );
}

export function IconChevronRight(props) {
  return (
    <Svg {...props}>
      <path d="m9 6 6 6-6 6" />
    </Svg>
  );
}

export function IconChevronLeft(props) {
  return (
    <Svg {...props}>
      <path d="m15 6-6 6 6 6" />
    </Svg>
  );
}

export function IconMenu(props) {
  return (
    <Svg {...props}>
      <path d="M4 8h16M4 16h16" />
    </Svg>
  );
}

export function IconClose(props) {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  );
}

export function IconCalendar(props) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="5" width="17" height="15" rx="3" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </Svg>
  );
}

export function IconLayers(props) {
  return (
    <Svg {...props}>
      <path d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path d="m3 13 9 5 9-5" />
    </Svg>
  );
}

export function IconActivity(props) {
  return (
    <Svg {...props}>
      <path d="M3 12h4l3-8 4 16 3-8h4" />
    </Svg>
  );
}

export function IconBolt(props) {
  return (
    <Svg {...props}>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </Svg>
  );
}

export function IconInfo(props) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.5v.01" />
    </Svg>
  );
}

export function IconAlert(props) {
  return (
    <Svg {...props}>
      <path d="M10.3 3.9 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.5v4M12 17v.01" />
    </Svg>
  );
}

export function IconCheckCircle(props) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12.5 2.8 2.8L16.5 9.5" />
    </Svg>
  );
}

export function IconMail(props) {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="m4 7 8 6 8-6" />
    </Svg>
  );
}

export function IconBank(props) {
  return (
    <Svg {...props}>
      <path d="M3 9.5 12 4l9 5.5M4.5 10v8M9.5 10v8M14.5 10v8M19.5 10v8M3 20h18" />
    </Svg>
  );
}

export function IconCoins(props) {
  return (
    <Svg {...props}>
      <circle cx="9" cy="12" r="5.5" />
      <path d="M13.2 7.6A5.5 5.5 0 1 1 13.2 16.4" />
    </Svg>
  );
}

export function IconSwap(props) {
  return (
    <Svg {...props}>
      <path d="M7 7h11l-3-3M17 17H6l3 3" />
    </Svg>
  );
}

export function IconArrowUpRight(props) {
  return (
    <Svg {...props}>
      <path d="M7 17 17 7M8 7h9v9" />
    </Svg>
  );
}

export function IconArrowUpDown(props) {
  return (
    <Svg {...props}>
      <path d="M8 4v16M4 8l4-4 4 4M16 20V4M12 16l4 4 4-4" />
    </Svg>
  );
}

export function IconRefresh(props) {
  return (
    <Svg {...props}>
      <path d="M20 11a8 8 0 1 0-2.3 5.7" />
      <path d="M20 4v7h-7" />
    </Svg>
  );
}
